import Knock from '@knocklabs/client'
import type { SetPreferencesProperties } from '@knocklabs/client'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { KNOCK_PUBLIC_KEY } from '@/constants/env'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { useNotificationToken } from '@/hooks/notifications/useNotificationToken'

import { getNotificationPreferencesQueryKey } from '@/notifications/knock/hooks/useNotificationPreferences'
import type { NotificationPreferenceSet } from '@/notifications/types'

const DEFAULT_PREFERENCES: Omit<NotificationPreferenceSet, 'id'> = {
  categories: {},
  channel_types: {},
  channels: {},
  workflows: {
    monitor: {
      channel_types: {
        email: true,
        in_app_feed: true,
      },
    },
    'new-comment': {
      channel_types: {
        in_app_feed: true,
      },
    },
    'new-pursuit': {
      channel_types: {
        in_app_feed: true,
      },
    },
    'new-reply': {
      channel_types: {
        in_app_feed: true,
      },
    },
    'new-task': {
      channel_types: {
        in_app_feed: true,
      },
    },
    'pursuit-notice': {
      channel_types: {
        email: true,
      },
    },
    schedule: {
      channel_types: {
        email: true,
      },
    },
    'shared-thread': {
      channel_types: {
        in_app_feed: true,
      },
    },
    changelog: {
      channel_types: {
        in_app_feed: true,
      },
    },
    'unread-notifications': {
      channel_types: {
        email: true,
      },
    },
    'upcoming-pursuit': {
      channel_types: {
        in_app_feed: true,
      },
    },
    'upcoming-task': {
      channel_types: {
        in_app_feed: true,
      },
    },
  },
}

export const useUpdateNotificationPreferences = () => {
  const { user } = useAuthInfo()
  const { data: token } = useNotificationToken()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    [NotificationPreferenceSet],
    NotificationPreferenceSet
  >({
    mutationFn: async ([preferences]) => {
      if (!user || token === undefined) {
        return
      }

      const knockClient = new Knock(KNOCK_PUBLIC_KEY)
      knockClient.authenticate(user.userId, token)
      await knockClient.user.setPreferences({
        categories: preferences.categories || {},
        channel_types: preferences.channel_types || {},
        channels: preferences.channels || {},
        workflows: preferences.workflows || {},
      } as SetPreferencesProperties)
    },
    onMutate: async ([preferences]) => {
      const key = getNotificationPreferencesQueryKey(user?.userId)
      await queryClient.cancelQueries({ queryKey: key })

      // snapshot the previous value
      const previousData =
        queryClient.getQueryData<NotificationPreferenceSet>(key)

      // optimistically update
      if (previousData) {
        queryClient.setQueryData<NotificationPreferenceSet>(key, {
          ...preferences,
          categories: preferences.categories || {},
          channel_types: preferences.channel_types || {},
          workflows: preferences.workflows || {},
        })
      }

      return (
        previousData ?? {
          ...DEFAULT_PREFERENCES,
          id: preferences.id,
        }
      )
    },
    onError: (err, [preferences], context) => {
      const key = getNotificationPreferencesQueryKey(user?.userId)
      queryClient.setQueryData(key, preferences)
      handleError(err)
    },
  })
}
